import axios from 'axios';
import { useCallback, useRef } from 'react';
import { createApi } from '@truescope-web/utils/lib/api';
import { isNullOrUndefined } from '@truescope-web/utils/lib/objects';
import { stringIsNullOrEmpty } from '@truescope-web/utils/lib/strings';
import config from '../config.json';

const createLazyTokenizedApi = (getAccessTokenSilently, { baseUrl, audience }, headers) => {
	return () =>
		getAccessTokenSilently({
			audience
		})
			.then((token) => createApi(baseUrl, { ...headers, token }))
			.catch((fail) => {
				console.error('failed to get token, logging in with redirect', fail);
				return null;
			});
};

export const createAuthenticatedApiLookup = (getAccessTokenSilently, headers) => {
	return {
		getClientApi: createLazyTokenizedApi(getAccessTokenSilently, config.api.client, headers)
	};
};

export const createUnauthenticatedApiLookup = () => {
	return {
		getClientApi: () => Promise.resolve(createApi(config.api.client.baseUrl))
	};
};

/**
 * extracts an error from an api exception
 * @param {*} param0
 */
export const extractError = (exception) => {
	if (!stringIsNullOrEmpty(exception.response?.data?.message)) {
		console.error('An api error occurred: ', exception.response.data.message, exception.response.data.details);
		return exception.response.data.message;
	}

	if (!stringIsNullOrEmpty(exception.message)) {
		console.error(`An error occurred - ${exception.message}`, exception);
		return exception.message;
	}

	console.error('An unknown error occurred', exception);
	return 'serious error occurred';
};

/**
 * cancelable token for use with axios, this is a little different to the one above
 * it doesn't use the form to continuously reset the token. it keeps the token separate
 * this is useful for situations where the form and the token are not synchronized
 */
export const useCancelToken = () => {
	const cancelToken = useRef();

	const cancel = useCallback(() => {
		if (isNullOrUndefined(cancelToken.current) || cancelToken.current.token?.reason?.message === 'cancelled') {
			return;
		}

		cancelToken.current.cancel('cancelled');
	}, [cancelToken]);

	const reset = useCallback(() => {
		cancel();

		const nextToken = axios.CancelToken.source();
		cancelToken.current = nextToken;
		return nextToken.token;
	}, [cancelToken, cancel]);

	return {
		reset,
		cancel
	};
};

/**
 * checks to see if a cancellation token was used and cancelled
 * @param {object} cancelToken cancellation token
 * @returns true if present and cancelled
 */
export const wasTokenCancelled = (cancelToken) => cancelToken?.reason?.message === 'cancelled' || cancelToken?.message === 'cancelled';
